import type { Ruolo } from '@aulaviva/shared';
import { lazy, Suspense, type ReactNode } from 'react';
import { createBrowserRouter, data, redirect } from 'react-router';
import { comuni } from '@/testi';
import { queryMe } from '@/api/auth';
import { queryClient } from '@/app/query';
import { LayoutGestione } from '@/layout/LayoutGestione';
import { LayoutPartecipante } from '@/layout/LayoutPartecipante';
import { LayoutPubblico } from '@/layout/LayoutPubblico';
import { homePerRuolo } from '@/lib/ruoli';
import { Accedi } from '@/pagine/Accedi';
import { CodiceAutenticazione } from '@/pagine/CodiceAutenticazione';
import { Sicurezza } from '@/pagine/Sicurezza';
import { Impostazioni } from '@/pagine/admin/Impostazioni';
import { UtenteDettaglio } from '@/pagine/admin/UtenteDettaglio';
import { Attestati } from '@/pagine/Attestati';
import { Verifica } from '@/pagine/Verifica';
import { CompletaIscrizione } from '@/pagine/CompletaIscrizione';
import { ConfermaLink } from '@/pagine/ConfermaLink';
import { ErrorePagina } from '@/pagine/ErrorePagina';
import { IlMioPercorso } from '@/pagine/IlMioPercorso';
import { NonTrovata } from '@/pagine/NonTrovata';
import { NuovoDispositivo } from '@/pagine/NuovoDispositivo';
import { Profilo } from '@/pagine/Profilo';
import { Registrati } from '@/pagine/Registrati';
import { VerificaEmail } from '@/pagine/VerificaEmail';
import { WebinarDettaglio } from '@/pagine/WebinarDettaglio';
import { Percorsi } from '@/pagine/admin/Percorsi';
import { PercorsoDettaglio } from '@/pagine/admin/PercorsoDettaglio';
import { PercorsoForm } from '@/pagine/admin/PercorsoForm';
import { Utenti } from '@/pagine/admin/Utenti';
import { WebinarAdmin } from '@/pagine/admin/WebinarAdmin';
import { WebinarAdminDettaglio } from '@/pagine/admin/WebinarAdminDettaglio';
import { WebinarForm } from '@/pagine/admin/WebinarForm';
import { WebinarRegia } from '@/pagine/regia/WebinarRegia';
import { IMieiWebinar } from '@/pagine/relatore/IMieiWebinar';

// Pagine dell'aula: pdf.js e hls.js restano in chunk separati, caricati solo qui.
const Aula = lazy(() => import('@/pagine/aula/Aula').then((m) => ({ default: m.Aula })));
const ConsoleRelatore = lazy(() => import('@/pagine/relatore/ConsoleRelatore').then((m) => ({ default: m.ConsoleRelatore })));
const RegiaDettaglio = lazy(() => import('@/pagine/regia/RegiaDettaglio').then((m) => ({ default: m.RegiaDettaglio })));
const Diretta = lazy(() => import('@/pagine/admin/Diretta').then((m) => ({ default: m.Diretta })));
const TestStato = lazy(() => import('@/pagine/test/TestStato').then((m) => ({ default: m.TestStato })));
const Tentativo = lazy(() => import('@/pagine/test/Tentativo').then((m) => ({ default: m.Tentativo })));
const Esito = lazy(() => import('@/pagine/test/Esito').then((m) => ({ default: m.Esito })));

function Lazy({ children }: { children: ReactNode }) {
  return <Suspense fallback={<p className="t-body text-ink-muted">{comuni.caricamento}</p>}>{children}</Suspense>;
}

function redirectAccesso(request: Request) {
  const url = new URL(request.url);
  const dopo = url.pathname !== '/' ? `?dopo=${encodeURIComponent(url.pathname + url.search)}` : '';
  return redirect(`/accedi${dopo}`);
}

/** Area partecipante: serve una sessione; gli altri ruoli vanno alla loro home. */
async function richiedeAccesso({ request }: { request: Request }) {
  const utente = await queryMe(queryClient);
  if (!utente) throw redirectAccesso(request);
  if (utente.ruolo !== 'partecipante' && new URL(request.url).pathname === '/') throw redirect(homePerRuolo(utente.ruolo));
  return utente;
}

/** Aree di gestione: sessione e ruolo giusto, altrimenti 403 in tono brand. */
function richiedeRuolo(ruoli: Ruolo[]) {
  return async ({ request }: { request: Request }) => {
    const utente = await queryMe(queryClient);
    if (!utente) throw redirectAccesso(request);
    if (!ruoli.includes(utente.ruolo)) throw data({ area: ruoli[0], ruolo: utente.ruolo }, { status: 403 });
    return utente;
  };
}

/** Le pagine pubbliche con una sessione aperta portano alla home del ruolo. */
async function soloOspiti() {
  const utente = await queryMe(queryClient);
  if (utente) throw redirect(homePerRuolo(utente.ruolo));
  return null;
}

export const router = createBrowserRouter([
  {
    element: <LayoutPubblico />,
    errorElement: <ErrorePagina />,
    children: [
      { path: '/accedi', loader: soloOspiti, element: <Accedi /> },
      { path: '/registrati', loader: soloOspiti, element: <Registrati /> },
      { path: '/verifica-email', element: <VerificaEmail /> },
      { path: '/nuovo-dispositivo', element: <NuovoDispositivo /> },
      { path: '/codice-autenticazione', element: <CodiceAutenticazione /> },
      { path: '/conferma/:token', element: <ConfermaLink /> },
      { path: '/completa-iscrizione/:token', element: <CompletaIscrizione /> },
      { path: '/verifica', element: <Verifica /> },
      { path: '/verifica/:codice', element: <Verifica /> },
    ],
  },
  {
    element: <LayoutPartecipante />,
    errorElement: <ErrorePagina />,
    loader: richiedeAccesso,
    id: 'area',
    children: [
      { path: '/', element: <IlMioPercorso /> },
      { path: '/webinar/:id', element: <WebinarDettaglio /> },
      { path: '/webinar/:id/aula', element: <Lazy><Aula /></Lazy> },
      { path: '/webinar/:id/test', element: <Lazy><TestStato /></Lazy> },
      { path: '/test/:tentativoId', element: <Lazy><Tentativo /></Lazy> },
      { path: '/test/:tentativoId/esito', element: <Lazy><Esito /></Lazy> },
      { path: '/attestati', element: <Attestati /> },
      { path: '/profilo', element: <Profilo /> },
    ],
  },
  {
    element: <LayoutGestione />,
    errorElement: <ErrorePagina />,
    loader: richiedeRuolo(['admin']),
    id: 'gestione',
    children: [
      { path: '/admin', element: <Percorsi /> },
      { path: '/admin/percorsi/nuovo', element: <PercorsoForm /> },
      { path: '/admin/percorsi/:id', element: <PercorsoDettaglio /> },
      { path: '/admin/percorsi/:id/modifica', element: <PercorsoForm /> },
      { path: '/admin/webinar', element: <WebinarAdmin /> },
      { path: '/admin/webinar/nuovo', element: <WebinarForm /> },
      { path: '/admin/webinar/:id', element: <WebinarAdminDettaglio /> },
      { path: '/admin/webinar/:id/modifica', element: <WebinarForm /> },
      { path: '/admin/webinar/:id/diretta', element: <Lazy><Diretta /></Lazy> },
      { path: '/admin/utenti', element: <Utenti /> },
      { path: '/admin/utenti/:id', element: <UtenteDettaglio /> },
      { path: '/admin/impostazioni', element: <Impostazioni /> },
    ],
  },
  {
    element: <LayoutGestione />,
    errorElement: <ErrorePagina />,
    loader: richiedeRuolo(['admin', 'relatore', 'regia']),
    id: 'gestione-sicurezza',
    children: [{ path: '/sicurezza', element: <Sicurezza /> }],
  },
  {
    element: <LayoutGestione />,
    errorElement: <ErrorePagina />,
    loader: richiedeRuolo(['relatore', 'admin']),
    id: 'gestione-relatore',
    children: [
      { path: '/relatore', element: <IMieiWebinar /> },
      { path: '/relatore/webinar/:id', element: <Lazy><ConsoleRelatore /></Lazy> },
    ],
  },
  {
    element: <LayoutGestione />,
    errorElement: <ErrorePagina />,
    loader: richiedeRuolo(['regia', 'admin']),
    id: 'gestione-regia',
    children: [
      { path: '/regia', element: <WebinarRegia /> },
      { path: '/regia/webinar/:id', element: <Lazy><RegiaDettaglio /></Lazy> },
    ],
  },
  { path: '*', element: <NonTrovata /> },
]);
